import {IssueTemplateChooser} from '@github-ui/issues-shared/IssueTemplateChooser'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {useEntryPointsLoader} from '../../hooks/use-entrypoint-loaders'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import type {IssueRepoNewChoosePageContentInternal$key} from './__generated__/IssueRepoNewChoosePageContentInternal.graphql'
import type {IssueRepoNewChoosePageQuery} from './__generated__/IssueRepoNewChoosePageQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query IssueRepoNewChoosePageQuery($name: String!, $owner: String!) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...IssueRepoNewChoosePageContentInternal
    }
  }
`

export const IssueRepoNewChoosePage: EntryPointComponent<
  {
    pageQuery: IssueRepoNewChoosePageQuery
  },
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)

  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Issue Create">
      <IssueRepoNewChoosePageContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

export function IssueRepoNewChoosePageContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<IssueRepoNewChoosePageQuery>
}) {
  const pageData = usePreloadedQuery<IssueRepoNewChoosePageQuery>(PageQuery, pageQueryRef)
  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <IssueRepoNewChoosePageContentInternal repository={okResult.value} />
}

function IssueRepoNewChoosePageContentInternal({repository}: {repository: IssueRepoNewChoosePageContentInternal$key}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueRepoNewChoosePageContentInternal on Repository {
        hasIssuesEnabled
        id
        nameWithOwner
        issueForms {
          __typename
          filename
          name
          description
        }
        issueTemplates {
          __typename
          filename
          name
          about
        }
        isBlankIssuesEnabled
        isBlankIssuesMaintainerOverride
        isSecurityPolicyEnabled
        securityPolicyUrl
        contactLinks {
          name
          about
          url
        }
        hasAnyTemplates
        name
        owner {
          login
        }
      }
    `,
    repository,
  )

  return <IssueTemplateChooser repository={data} />
}
